/** Batch "Change owner" dialog for the Bookings grid (a custom `dialog(ctx)`
 *  batch action).
 *
 *  Reassigns each selected booking to a new owner (guest). A booking can only be
 *  reassigned to a user in its own tenant family — `Group_Tour_User.switch_user`
 *  hard-refuses a cross-tenant move — so the owner is picked scoped to a source
 *  operator (defaulting to the grid's tenant filter), and when the selection may
 *  reach outside that operator (`detectCrossTenant`) the only available behaviour
 *  is `ignore_others` (out-of-tenant bookings are skipped). We surface that as a
 *  single-option strategy note; submit fires the generic `/bookings/batch/`
 *  dispatch via `ctx.runStandard`. The server re-checks the cross-tenant
 *  condition and is authoritative (a 400 surfaces as a snackbar).
 */
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { ownerOptions, type BookingUserOption } from '@/api/bookings';
import { CrossTenantStrategyField } from '@/components/DataGrid/CrossTenantStrategyField';
import type { BatchActionContext } from '@/components/DataGrid/batchTypes';
import { detectCrossTenant } from '@/components/DataGrid/detectCrossTenant';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';

/** Reassigning an owner is never valid across tenants, so `ignore_others` is the
 *  only strategy (server-enforced). */
const OWNER_STRATEGIES = ['ignore_others'];

function ownerLabel(o: BookingUserOption): string {
  const name = `${o.first_name ?? ''} ${o.last_name ?? ''}`.trim();
  const contact = o.email || o.phone || `#${o.id}`;
  return name ? `${name} · ${contact}` : contact;
}

interface Props {
  ctx: BatchActionContext;
  /** The grid's current tenant filter (null when unfiltered). Default source
   *  operator + one input to the cross-tenant heuristic. */
  gridTenantId: number | null;
  /** Tenant id per selected row (ids mode) — the other input to the heuristic. */
  selectedRowTenantIds: Array<number | null>;
}

export function ChangeOwnerDialog({ ctx, gridTenantId, selectedRowTenantIds }: Props) {
  const [sourceOp, setSourceOp] = useState<TenantPickerValue | null>(
    gridTenantId != null ? { id: gridTenantId, name: '' } : null,
  );
  const [owner, setOwner] = useState<BookingUserOption | null>(null);
  const [strategy, setStrategy] = useState<string | null>(null);

  const sourceOpId = sourceOp?.id ?? null;
  // The owner is scoped to the source operator, so the value's tenant is that operator.
  const crossTenant =
    sourceOpId != null &&
    detectCrossTenant(sourceOpId, ctx.selection, {
      gridTenantFilter: gridTenantId,
      rows: selectedRowTenantIds.map((tenantId) => ({ tenantId })),
    });

  const needsStrategy = crossTenant && !strategy;
  const canSubmit = sourceOpId != null && owner != null && !needsStrategy;

  const submit = () => {
    if (!owner) return;
    const params: Record<string, unknown> = { target_user: owner.id };
    if (crossTenant && strategy) params.cross_tenant_strategy = strategy;
    void ctx.runStandard(params);
    ctx.close();
  };

  return (
    <Dialog open onClose={ctx.close} fullWidth maxWidth="sm">
      <DialogTitle>Change owner</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Reassign {ctx.count.toLocaleString()} booking(s) to a new owner. This runs in the
            background. Bookings outside the owner&apos;s tenant are skipped.
          </Typography>

          {gridTenantId == null && (
            <TenantPicker
              value={sourceOp}
              onChange={(t) => {
                setSourceOp(t);
                setOwner(null);
                setStrategy(null);
              }}
            />
          )}

          <RemoteAutocomplete<BookingUserOption>
            label="New owner"
            size="small"
            value={owner}
            getOptionLabel={ownerLabel}
            getOptionKey={(o) => o.id}
            queryFn={async (q, signal) => {
              if (sourceOpId == null || q.trim().length < 2) return [];
              return ownerOptions(sourceOpId, q, signal);
            }}
            onChange={(v) => setOwner(v as BookingUserOption | null)}
            placeholder="Search by name, email or phone…"
          />

          {crossTenant && (
            <CrossTenantStrategyField
              value={strategy}
              onChange={setStrategy}
              allowedStrategies={OWNER_STRATEGIES}
              noun="owner"
            />
          )}

          {ctx.selection.mode === 'all_matching' && (
            <Alert severity="info">
              Acting on {ctx.matchedPhrase} bookings.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={ctx.close}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={submit}>
          Change owner
        </Button>
      </DialogActions>
    </Dialog>
  );
}
